import React from 'react';
import { useQueryStates, parseAsString, parseAsInteger } from 'nuqs';
import { useAtom } from 'jotai';

import { usersHydratedAtom, usersListParamsAtom } from '@/store';

export interface SearchFields {
  first_name: string;
  last_name: string;
  email: string;
  organization: string;
}

export function useUsersFilters() {
  const [params, setParams] = useAtom(usersListParamsAtom);
  const [, setHydrated] = useAtom(usersHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    search: parseAsString.withDefault(''),
    role: parseAsString.withDefault('all'),
    verified: parseAsString.withDefault('all'),
    first_name: parseAsString.withDefault(''),
    last_name: parseAsString.withDefault(''),
    email: parseAsString.withDefault(''),
    organization: parseAsString.withDefault(''),
    date: parseAsString.withDefault(''),
    'filter[email]': parseAsString.withDefault('')
  });

  // Filters state (client UI) mapped to API params (URL synced)
  const [roleFilter, setRoleFilter] = React.useState<string>(queryParams.role);
  const [verifiedFilter, setVerifiedFilter] = React.useState<string>(queryParams.verified);
  const [searchFields, setSearchFields] = React.useState<SearchFields>({
    first_name: queryParams.first_name,
    last_name: queryParams.last_name,
    email: queryParams.email,
    organization: queryParams.organization
  });

  // Search state
  const [searchTerm, setSearchTerm] = React.useState(queryParams.search);

  // Date filter state
  const [dateFilter, setDateFilter] = React.useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  });

  // Sync URL parameters with params atom on mount and when URL changes
  React.useEffect(() => {
    const filters: Record<string, string | undefined> = {};

    // Build filters from URL parameters
    if (queryParams.search) filters.search = queryParams.search;
    if (queryParams.role !== 'all') filters.role = queryParams.role;
    if (queryParams.verified !== 'all') filters.verified = queryParams.verified;
    if (queryParams.first_name) filters.first_name = queryParams.first_name;
    if (queryParams.last_name) filters.last_name = queryParams.last_name;
    if (queryParams.email) filters.email = queryParams.email;
    if (queryParams.organization) filters.organization = queryParams.organization;
    if (queryParams.date) filters.date = queryParams.date;
    // Handle filter[email] from URL params
    if (queryParams['filter[email]']) filters.email = queryParams['filter[email]'];

    setParams((prev) => ({
      ...prev,
      page: queryParams.page,
      filters: filters
    }));

    // Mark hydrated after first sync
    setHydrated(true);
  }, [queryParams.page, queryParams.search, queryParams.role, queryParams.verified, queryParams.first_name, queryParams.last_name, queryParams.email, queryParams.organization, queryParams.date, queryParams['filter[email]'], setParams]);

  // Sync URL parameters with local state on mount
  React.useEffect(() => {
    setSearchTerm(queryParams.search);
    setRoleFilter(queryParams.role);
    setVerifiedFilter(queryParams.verified);
    setSearchFields({
      first_name: queryParams.first_name,
      last_name: queryParams.last_name,
      email: queryParams.email,
      organization: queryParams.organization
    });
  }, []); // Only run on mount

  React.useEffect(() => {
    if (queryParams.date) {
      const dates = queryParams.date.split(',');
      if (dates.length === 2) {
        setDateFilter({
          start: dates[0] ? new Date(dates[0]) : null,
          end: dates[1] ? new Date(dates[1]) : null
        });
      } else if (dates.length === 1 && dates[0]) {
        setDateFilter({
          start: new Date(dates[0]),
          end: null
        });
      } else {
        setDateFilter({ start: null, end: null });
      }
    } else {
      setDateFilter({ start: null, end: null });
    }
  }, [queryParams.date]);

  // Search handler
  const handleSearch = React.useCallback(() => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: searchTerm || undefined }
    }));
    setQueryParams({ page: 1, search: searchTerm });
  }, [searchTerm, setParams, setQueryParams]);

  // Clear search handler
  const handleClearSearch = React.useCallback(() => {
    setSearchTerm('');
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, search: undefined }
    }));
    setQueryParams({ page: 1, search: '' });
  }, [setParams, setQueryParams]);

  const applyFilters = React.useCallback(async () => {
    const nextFilters: Record<string, string | undefined> = {
      ...params.filters,
      role: roleFilter !== 'all' ? roleFilter : undefined,
      verified: verifiedFilter !== 'all' ? verifiedFilter : undefined,
      first_name: searchFields.first_name || undefined,
      last_name: searchFields.last_name || undefined,
      email: searchFields.email || undefined,
      organization: searchFields.organization || undefined
    };

    setParams((prev) => ({ ...prev, page: 1, filters: nextFilters }));

    await setQueryParams({
      page: 1,
      role: roleFilter,
      verified: verifiedFilter,
      first_name: searchFields.first_name,
      last_name: searchFields.last_name,
      email: searchFields.email,
      organization: searchFields.organization
    });

  }, [params.filters, roleFilter, verifiedFilter, searchFields, setParams, setQueryParams]);

  const clearFilters = React.useCallback(async () => {
    setSearchTerm('');
    setRoleFilter('all');
    setVerifiedFilter('all');
    setSearchFields({ first_name: '', last_name: '', email: '', organization: '' });
    setDateFilter({ start: null, end: null });
    setParams((prev) => ({ ...prev, page: 1, filters: {} }));
    await setQueryParams({
      page: 1,
      search: '',
      role: 'all',
      verified: 'all',
      first_name: '',
      last_name: '',
      email: '',
      organization: '',
      date: '',
      'filter[email]': ''
    });
  }, [setParams, setQueryParams]);

  const hasAnyFilter = React.useMemo(() => {
    return (
      Boolean(searchTerm) ||
      roleFilter !== 'all' ||
      verifiedFilter !== 'all' ||
      Boolean(searchFields.first_name || searchFields.last_name || searchFields.email || searchFields.organization) ||
      Boolean(dateFilter.start || dateFilter.end)
    );
  }, [searchTerm, roleFilter, verifiedFilter, searchFields, dateFilter]);

  const handleRoleChange = React.useCallback((val: string) => {
    setRoleFilter(val);
    setQueryParams({ page: 1, role: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, role: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handleVerifiedChange = React.useCallback((val: string) => {
    setVerifiedFilter(val);
    setQueryParams({ page: 1, verified: val });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: { ...prev.filters, verified: val !== 'all' ? val : undefined }
    }));
  }, [setQueryParams, setParams]);

  const handlePageChange = React.useCallback((page: number) => {
    setParams((prev) => ({ ...prev, page }));
    setQueryParams({ page });
  }, [setParams, setQueryParams]);

  const handleDateChange = React.useCallback((value: { start: Date | null; end: Date | null }) => {
    setDateFilter(value);

    let dateFilterValue = '';
    if (value.start && value.end) {
      // Format dates as YYYY-MM-DD
      const startDate = value.start.toISOString().split('T')[0];
      const endDate = value.end.toISOString().split('T')[0];
      dateFilterValue = `${startDate},${endDate}`;
    } else if (value.start) {
      dateFilterValue = value.start.toISOString().split('T')[0];
    }

    setQueryParams({ page: 1, date: dateFilterValue });
    setParams((prev) => ({
      ...prev,
      page: 1,
      filters: {
        ...prev.filters,
        date: dateFilterValue || undefined
      }
    }));
  }, [setParams, setQueryParams]);

  return {
    // State
    searchTerm,
    setSearchTerm,
    roleFilter,
    verifiedFilter,
    searchFields,
    setSearchFields,
    dateFilter,
    hasAnyFilter,

    // Actions
    handleSearch,
    handleClearSearch,
    applyFilters,
    clearFilters,
    handleRoleChange,
    handleVerifiedChange,
    handleDateChange,
    handlePageChange
  };
}

